<script lang="ts" setup>
import { AppButton, AppTextField } from '@/core/components';
import type { FormErrors } from '@/types/Form';
import { computed } from 'vue';
import type { ForgotPasswordEmailForm } from '../../types/Auth';

const props = withDefaults(
  defineProps<{
    username?: string;
  }>(),
  {
    username: '',
  }
);

const { validations } = useValidation();

const { form, handleSubmit, getErrors } = useForm<ForgotPasswordEmailForm>({
  form: {
    username: props.username ?? '',
  },
  rules: {
    username: {
      email: validations.email(),
      required: validations.required('Email address'),
    },
  },
});

const {
  data,
  error: serverError,
  pending: loadingForgotPassword,
  execute: execForgotPassword,
} = useSubmit<{}, { data: FormErrors<ForgotPasswordEmailForm> }>();
loadingForgotPassword.value = false;

const emits = defineEmits<{
  (e: 'next', username: string): void;
}>();

const handleSignUp = async (): Promise<void> => {
  handleSubmit(async () => {
    await execForgotPassword('/auth/forgot-password', {
      method: 'POST',
      body: form.value,
    });

    if (!serverError.value) {
      emits('next', form.value.username);
    }
  });
};

const errors = computed(() => {
  return getErrors({ username: serverError.value?.data?.errors.username });
});
</script>

<template>
  <v-form @submit.prevent="handleSignUp">
    <AppTextField
      v-model="form.username"
      data-testid="email"
      label="Email Address"
      :error-messages="errors.username"
      :disabled="loadingForgotPassword || username !== ''"
      autofocus
      data-cy="input-email"
    >
    </AppTextField>

    <AppButton block class="mt-4 font-weight-bold" :loading="loadingForgotPassword" data-testid="submit">
      Submit
    </AppButton>
  </v-form>
</template>

<style lang="scss" scoped>
.v-card {
  margin-top: 132px;
}
</style>
